<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { isBoolean } from 'lodash';

const props = withDefaults(
  defineProps<{
    collapsible?: boolean;
    caption?: string | null;
    stateKey?: string | null;
    carousel?: boolean;
    initialState?: string;
  }>(),
  {
    collapsible: false,
    caption: null,
    stateKey: null,
    carousel: false,
    initialState: 'expanded',
  },
);

const instance = getCurrentInstance();
const proxy = instance?.proxy as any;
const $app = computed(() => proxy?.$root?.$app);

function getIsExpanded(): boolean {
  if (props.collapsible && props.stateKey) {
    const value = $app.value?.objectStore?.getItem(
      `panel.${props.stateKey}`,
      null,
    );
    if (value && isBoolean(value.isExpanded)) {
      return value.isExpanded;
    }
  }
  return props.initialState === 'expanded';
}

const isExpanded = ref(getIsExpanded());

watch(isExpanded, (value) => {
  if (props.collapsible && props.stateKey) {
    $app.value?.objectStore?.setItem(`panel.${props.stateKey}`, {
      isExpanded: value,
    });
  }
});

const className = computed(() => ({
  'itp-panel': true,
  'w-100': true,
  collapsible: props.collapsible,
  expanded: isExpanded.value,
}));

const iconName = computed(() => {
  if (props.carousel) {
    return isExpanded.value
      ? 'material-symbols:expand-all'
      : 'material-symbols:collapse-all';
  }
  return isExpanded.value ? 'fa-angle-down' : 'fa-angle-right';
});

const INTERACTIVE_SELECTOR = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="menu"]',
  '.dropdown',
  '.dropdown-toggle',
  '.dropdown-menu',
  '.form-control',
  '.form-select',
  '.itp-no-panel-toggle',
].join(',');

function onClick(event?: MouseEvent) {
  if (!props.collapsible) {
    return;
  }
  // The interactive-descendant guard only matters when the click bubbled
  // up through the whole header (e.g. clicks on the JobHeader's BDropdown
  // shouldn't toggle). When the chevron icon emits `click` directly we
  // get the event on the icon itself and just toggle.
  const target = event?.target as HTMLElement | null;
  if (target && typeof target.closest === 'function') {
    const interactive = target.closest(INTERACTIVE_SELECTOR);
    if (interactive && interactive !== event!.currentTarget) {
      return;
    }
  }
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <div :class="className">
    <ItpBox
      align-items="center"
      inline
      :class="{
        'w-100': collapsible && carousel,
      }"
    >
      <ItpIcon
        v-if="collapsible && !carousel"
        class="ms-2 me-3 text-muted"
        :style="{ width: '1em' }"
        :name="iconName"
        :clickable="true"
        @click="onClick"
      />

      <slot name="caption">
        <strong>{{ caption }}</strong>
      </slot>

      <ItpButton
        v-if="collapsible && carousel"
        variant="default"
        size="sm"
        class="ms-auto"
        no-tab
        @click="onClick"
      >
        <ItpIcon
          class="text-muted"
          :style="{ width: '1em' }"
          :name="iconName"
        />
      </ItpButton>
    </ItpBox>

    <div v-show="isExpanded">
      <slot></slot>
    </div>
  </div>
</template>
